import { useCallback, useMemo, useState } from "react";
import { useLatestRef } from "../../hooks/use-latest-ref";
import type { PaginationItem } from "./Pagination.types";
import { buildPaginationItems } from "./pagination-items";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UsePaginationOptions {
  /** Total number of items across every page. */
  readonly totalItems: number;
  /** Current 1-based page (controlled). Omit to let the hook own the page. */
  readonly page?: number | undefined;
  /** Initial 1-based page when uncontrolled. Defaults to 1. */
  readonly defaultPage?: number | undefined;
  readonly onPageChange?: ((page: number) => void) | undefined;
  /** Items per page (controlled). Omit to let the hook own the page size. */
  readonly pageSize?: number | undefined;
  /** Initial items per page when uncontrolled. Defaults to 10. */
  readonly defaultPageSize?: number | undefined;
  readonly onPageSizeChange?: ((pageSize: number) => void) | undefined;
  /** Pages kept either side of the current page. Defaults to 1. */
  readonly siblingCount?: number | undefined;
  /** Pages kept at each end of the list. Defaults to 1. */
  readonly boundaryCount?: number | undefined;
}

export interface UsePaginationReturn {
  /** Current 1-based page, clamped to the page count. */
  readonly page: number;
  readonly pageSize: number;
  /** Number of pages; at least 1, even with no items. */
  readonly pageCount: number;
  readonly totalItems: number;
  /** 1-based index of the first item on the current page; 0 when there are none. */
  readonly rangeStart: number;
  /** 1-based index of the last item on the current page; 0 when there are none. */
  readonly rangeEnd: number;
  readonly canGoPrevious: boolean;
  readonly canGoNext: boolean;
  readonly goToPage: (page: number) => void;
  readonly goToPreviousPage: () => void;
  readonly goToNextPage: () => void;
  /** Changes the page size, keeping the first item of the current page in view. */
  readonly changePageSize: (pageSize: number) => void;
  /** The page list to render, including the ellipses standing in for gaps. */
  readonly items: readonly PaginationItem[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 10;

/**
 * Paging state: which page is current, how many pages there are, which item
 * range that covers, and the page list to render.
 *
 * `Pagination` uses this internally, so reach for it directly only when the
 * paging state has to live outside the control — driving the page from a route
 * param, or paginating a list that renders its own controls.
 */
export function usePagination(options: UsePaginationOptions): UsePaginationReturn {
  const {
    totalItems,
    page: controlledPage,
    defaultPage = 1,
    onPageChange,
    pageSize: controlledPageSize,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    onPageSizeChange,
    siblingCount = 1,
    boundaryCount = 1,
  } = options;

  const [uncontrolledPage, setUncontrolledPage] = useState(defaultPage);
  const [uncontrolledPageSize, setUncontrolledPageSize] = useState(defaultPageSize);

  const pageIsControlled = controlledPage !== undefined;
  const pageSizeIsControlled = controlledPageSize !== undefined;

  const total = Math.max(0, Math.trunc(totalItems));
  const pageSize = Math.max(1, Math.trunc(controlledPageSize ?? uncontrolledPageSize));
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Clamped on read rather than synced in an Effect: the total can shrink under
  // a stale page (a filter is applied, rows are deleted), and clamping here
  // keeps the rendered page valid without an extra render pass.
  const page = clampPage(controlledPage ?? uncontrolledPage, pageCount);

  const pageRef = useLatestRef(page);
  const pageSizeRef = useLatestRef(pageSize);
  const pageCountRef = useLatestRef(pageCount);
  const totalRef = useLatestRef(total);
  const onPageChangeRef = useLatestRef(onPageChange);
  const onPageSizeChangeRef = useLatestRef(onPageSizeChange);

  const goToPage = useCallback(
    (requested: number) => {
      const target = clampPage(requested, pageCountRef.current);
      if (target === pageRef.current) return;

      if (!pageIsControlled) setUncontrolledPage(target);
      onPageChangeRef.current?.(target);
    },
    [pageIsControlled, pageRef, pageCountRef, onPageChangeRef],
  );

  const goToPreviousPage = useCallback(() => {
    goToPage(pageRef.current - 1);
  }, [goToPage, pageRef]);

  const goToNextPage = useCallback(() => {
    goToPage(pageRef.current + 1);
  }, [goToPage, pageRef]);

  const changePageSize = useCallback(
    (requested: number) => {
      const nextPageSize = Math.max(1, Math.trunc(requested));
      if (nextPageSize === pageSizeRef.current) return;

      // The row the user was looking at stays on screen. Snapping back to page
      // one instead would lose their place in the list every time they widened
      // the page, which is usually the opposite of why they widened it.
      const firstItemIndex = (pageRef.current - 1) * pageSizeRef.current;
      const nextPageCount = Math.max(1, Math.ceil(totalRef.current / nextPageSize));
      const nextPage = clampPage(Math.floor(firstItemIndex / nextPageSize) + 1, nextPageCount);

      if (!pageSizeIsControlled) setUncontrolledPageSize(nextPageSize);
      onPageSizeChangeRef.current?.(nextPageSize);

      if (nextPage === pageRef.current) return;
      if (!pageIsControlled) setUncontrolledPage(nextPage);
      onPageChangeRef.current?.(nextPage);
    },
    [pageIsControlled, pageSizeIsControlled, pageRef, pageSizeRef, totalRef, onPageChangeRef, onPageSizeChangeRef],
  );

  const items = useMemo(
    () => buildPaginationItems({ pageCount, page, siblingCount, boundaryCount }),
    [pageCount, page, siblingCount, boundaryCount],
  );

  return {
    page,
    pageSize,
    pageCount,
    totalItems: total,
    rangeStart: total === 0 ? 0 : (page - 1) * pageSize + 1,
    rangeEnd: total === 0 ? 0 : Math.min(page * pageSize, total),
    canGoPrevious: page > 1,
    canGoNext: page < pageCount,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    changePageSize,
    items,
  };
}

function clampPage(value: number, pageCount: number): number {
  return Math.min(Math.max(Math.trunc(value), 1), pageCount);
}
